/**
 * Access token in memory; session continuity from an HttpOnly refresh cookie.
 *
 * The token used to live in localStorage, where any script that gets onto the
 * page can read it — one XSS and the session walks out. It sits in a module
 * variable now, which a script cannot reach and a page reload wipes. The reload
 * is the catch, and `restoreSession()` is the answer: the backend also sets a
 * refresh cookie at login (HttpOnly, SameSite=Strict, scoped to /api/v1/auth),
 * and that cookie buys a fresh access token without the password.
 *
 * Every refresh rotates the cookie, and a spent token coming back revokes the
 * whole family — see backend/auth/refresh.py. So a stolen cookie ends both
 * sessions rather than quietly sharing the account.
 *
 * The bare `fetch` calls here are deliberate. api.ts is the wrapper for
 * everything else and imports this module; calling back into it would be a
 * cycle. These two requests carry no auth header — the cookie is the
 * credential — so there is nothing for the wrapper to add.
 */
import { API_BASE } from '@/lib/apiBase';
import * as impersonation from '@/lib/impersonation';
import { wisConcept } from '@/lib/melden/concept';

let accessToken: string | null = null;

/**
 * The token this tab should be sending.
 *
 * A look-along token outranks the operator's own, and the precedence lives
 * here rather than at the call sites because every one of them — the request
 * wrapper's Authorization header, ProtectedRoute's is-anyone-signed-in check,
 * the realtime hook — has to agree about which session this tab is in. One of
 * them disagreeing means the operator sees the customer's screens carrying
 * their own full-rights credential.
 */
export function getToken(): string | null {
  return impersonation.get()?.token ?? accessToken;
}

/**
 * A deliberate sign-in on this tab. That is what ends a look-along here.
 *
 * `startLookAlong` holds the invariant one way round: a look-along tab gives up
 * the operator's credential. This is the mirror, and it was a convention rather
 * than a guard: /login and /accept-invite sit outside ProtectedRoute and the
 * band renders above them, so an operator who wants back into their own account
 * signs in right there — and the tab then held both, with getToken()'s
 * precedence still serving the look-along token. They would land on the
 * customer's dashboard believing they had just switched.
 *
 * `restoreSession` is not this case: it refuses during a look-along and never
 * reaches here. Only a sign-in does, and a sign-in means "be me on this tab".
 */
export function setToken(token: string): void {
  impersonation.clear();
  accessToken = token;
}

export function clearToken(): void {
  accessToken = null;
}

/**
 * Enter a look-along on THIS tab, and give up the operator's own credential
 * while doing it.
 *
 * The invariant is "a look-along tab never holds an operator token", and
 * `restoreSession`'s fence alone does not hold it: the fence only refuses once
 * a look-along is already present, so it depends on main.tsx reading the hash
 * before the startup refresh runs. Reorder those two lines and the tab acquires
 * a full-rights token, and from there a 401 racing the clear re-issues a
 * refused write as the operator. Dropping the token here holds the invariant
 * whatever the order is, which is why it lives at the moment of entry rather
 * than in a comment about ordering.
 *
 * Only main.tsx calls this. `impersonation.set()` is the raw store write and
 * stays available to tests.
 */
export function startLookAlong(token: string, tenantName: string, expiresAt: string | null = null): void {
  accessToken = null;
  impersonation.set(token, tenantName, expiresAt);
}

/**
 * Is anyone signed in on this tab? ProtectedRoute's gate.
 *
 * Reads `getToken()`, not `accessToken`, and the difference is the whole
 * feature: a freshly opened look-along tab has a look-along token and NO
 * operator token (the refresh is deliberately fenced off, so nothing ever
 * fills `accessToken` there). Reading the raw variable bounced that tab
 * straight to /login -- the look-along worked, was stored, cleared its hash,
 * and then showed a login screen. Found by opening it in a browser; no unit
 * test saw it, because each half was correct on its own.
 */
export function isAuthenticated(): boolean {
  return !!getToken();
}

/**
 * Trade the refresh cookie for an access token. Returns false when there is no
 * usable session — which is the normal case for a first-time visitor, not an
 * error worth showing anyone.
 *
 * Called once before the app renders (main.tsx), so a page reload continues the
 * session instead of bouncing to /login, and again by api.ts when a request
 * comes back 401 because the access token expired mid-session.
 */
export async function restoreSession(): Promise<boolean> {
  // THE fence of this feature, and it is one line because there is exactly one
  // function that trades the cookie for a token.
  //
  // The refresh cookie belongs to the OPERATOR and buys a full-rights 72-hour
  // token. Run this during a look-along and an F5 — or simply minute 31, where
  // every request starts coming back 401 — would replace the read-only token
  // with that one and re-issue the request. The refused write then succeeds,
  // as the operator, against the customer's data. Read-only would mean
  // "read-only until the clock runs out", which is not a promise worth making.
  //
  // Both callers are covered from here: main.tsx at startup, and api.ts's
  // refresh-and-retry on a 401. The login screens do not come through here —
  // they call setToken() after a deliberate sign-in, which is correct.
  if (impersonation.get()) return false;

  try {
    const response = await fetch(`${API_BASE}/api/v1/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });
    if (!response.ok) {
      clearToken();
      return false;
    }
    const body = (await response.json()) as { access_token?: string };
    if (!body.access_token) {
      clearToken();
      return false;
    }
    setToken(body.access_token);
    return true;
  } catch {
    // Offline or the API is down. Not a logout: leave whatever token we have.
    return false;
  }
}

/** Login route as a full path, honouring the Vite base the app is served under
 *  ('/app/' in production, '/' in dev). Hard redirects must go through this: a
 *  bare '/login' leaves the app entirely now that the marketing site owns '/'.
 *  Router-internal navigation needs no prefix — BrowserRouter's basename adds
 *  it — so this is only for window.location. */
export const LOGIN_URL = `${import.meta.env.BASE_URL}login`;

/**
 * End the session here and on the server. Only this session: signing out on a
 * laptop should not close the one on a phone, which is why the backend revokes
 * the single token rather than the family.
 *
 * The redirect happens whatever the server says. A logout that appears to fail
 * because the network blipped would leave someone looking at a signed-in screen
 * they asked to leave.
 */
export function logout(): void {
  // The unsent report draft is not tied to a user: the next person on this browser must not see it.
  wisConcept();
  // In a look-along tab, "sign out" means stop looking along -- and it must
  // NOT reach the logout endpoint. That endpoint revokes the refresh COOKIE,
  // which is the operator's own session and is shared across every tab, so
  // this one click would sign them out everywhere. The banner's stop button
  // was written with that in mind; the sidebar's own sign-out is the same
  // door and was not. Found by opening a look-along and looking at the
  // sidebar, which still offers it.
  //
  // The call carries no Authorization header (credentials: 'include' and
  // nothing else), so the backend cannot tell this apart from an ordinary
  // sign-out. This is the only place it can be stopped.
  if (impersonation.get()) {
    impersonation.clear();
    window.location.href = import.meta.env.BASE_URL;
    return;
  }
  clearToken();
  void fetch(`${API_BASE}/api/v1/auth/logout`, {
    method: 'POST',
    credentials: 'include',
  }).catch(() => undefined);
  window.location.href = LOGIN_URL;
}
